import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {ActivityIndicator, StyleSheet, View} from 'react-native';
import {zodResolver} from '@hookform/resolvers/zod';
import {observer} from 'mobx-react-lite';

import BackButton from '../../components/rewards/BackButton';
import CheckBox from '../../components/rewards/CheckBox';
import RewardsBanner from '../../components/rewards/RewardsBanner';
import StaffStorePicker from '../../components/StaffStorePicker';
import Button from '../../components/themed/Button';
import DateOfBirthInput from '../../components/themed/DateOfBirthInput';
import KeyboardAwareScrollView from '../../components/themed/KeyboardAwareScrollView';
import Text from '../../components/themed/Text';
import SuburbPicker from '../../components/ui-controls/address/SuburbPicker';
import FieldFrame from '../../design-system/form/FieldFrame';
import FormField from '../../design-system/form/FormField';
import * as designTokens from '../../design-system/tokens';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {AppRoute} from '../../models/AppRoute';
import {Shop} from '../../models/middleware';
import {InformationContentType, RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import apiService from '../../transport/apiService';
import {formatDateForZen} from '../../utils/date';
import {logger, serializeError} from '../../utils/logger';
import {formatMobileForDisplay} from '../../utils/phoneNumber';
import {rewardsMarketingName} from '../../utils/rewards';
import {
  DOB_HELPER,
  MOBILE_HELPER,
} from '../account/AccountCreateScreen/AccountCreateScreenLoyaltyEnabled';
import {
  emailSchema,
  RewardsSignUpFormData,
  rewardsSignUpSchema,
} from '../account/schemas/authSchemas';
import SignUpPageLayout, {
  CTA_HEIGHT,
  CTA_RADIUS,
  useWideLayout,
} from '../account/signUp/SignUpPageLayout';

import {styles} from './styles';
import {useRewardsTermsAndConditionsPopper} from './TermsAndConditions';

const INTRO =
  'Complete your profile to access member pricing, special offers, and instant savings.';

const RewardsSignUp = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_SIGN_UP>) => {
    const {rewardsStore, offersStore} = useStores();
    const {flowType} = route.params || {};
    const [selectedStaffStore, setSelectedStaffStore] = useState<Shop | null>(null);
    const [staffStoreError, setStaffStoreError] = useState<string | null>(null);
    const [emailValidating, setEmailValidating] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const tabBarPadding = useTabBarBottomPadding();
    const {isDesktopCard: isWide, onLayout} = useWideLayout();

    // DIS-1660: the sign up code is URL-supplied and validated on load by
    // RewardsSignInScreen. Here it is display-only, and only when it still
    // matches a currently loaded offer — an absent, empty or unread offers
    // list keeps the field hidden rather than showing an unverified code.
    const signUpOffers = offersStore.signUpOfferData;
    const isSignUpCodeValid = Boolean(
      rewardsStore.signUpCode &&
      signUpOffers?.some(offer => offer.signUpCode === rewardsStore.signUpCode),
    );

    const mountedRef = useRef(true);
    const emailValidationReqIdRef = useRef(0);

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    const handleStaffStoreSelected = useCallback(
      (shop: Shop) => {
        setSelectedStaffStore(shop);
        setStaffStoreError(null);
        rewardsStore.setStaffStoreId(shop.store_number);
        // DIS-1236: persist full Shop alongside id so RewardsVerificationScreen
        // can short-circuit phase 3 directly (no redundant re-pick) and render
        // the "YOUR SELECTED STORE" affordance for SIGNUP_LITE staff.
        rewardsStore.setStaffStore(shop);
      },
      [rewardsStore],
    );

    const {dialogNode, textNode} = useRewardsTermsAndConditionsPopper({
      text: 'Terms and Conditions',
      contentType: InformationContentType.REWARDS_PROGRAM_TERMS_AND_CONDITIONS,
    });

    const {
      control,
      handleSubmit,
      formState: {errors, touchedFields, isSubmitting, isSubmitted},
      getValues,
      setError,
    } = useForm<RewardsSignUpFormData>({
      resolver: zodResolver(rewardsSignUpSchema),
      defaultValues: {
        firstName: '',
        lastName: '',
        email: '',
        dateOfBirth: null,
        phoneNumber: rewardsStore.phoneNumber,
        confirmCheckbox: false,
        suburb: null,
        signUpCode: rewardsStore.signUpCode || '',
      },
      mode: 'onBlur',
    });

    const handleEmailBlur = async (formOnBlur: () => void, value: string) => {
      formOnBlur();
      const formatResult = emailSchema.safeParse(value);
      if (!formatResult.success) return;
      const myReqId = ++emailValidationReqIdRef.current;
      setEmailValidating(true);
      try {
        const response = await apiService.validateEmail(value);
        if (!mountedRef.current || myReqId !== emailValidationReqIdRef.current) return;
        if (!response.data.valid) {
          setError('email', {
            type: 'validate',
            message: response.data.message || 'Please enter a valid email address',
          });
        }
      } catch (e) {
        if (!mountedRef.current || myReqId !== emailValidationReqIdRef.current) return;
        logger.debug('SignUp', 'Email domain validation failed', serializeError(e));
        setError('email', {
          type: 'validate',
          message: 'Unable to validate email. Please try again.',
        });
      } finally {
        if (mountedRef.current && myReqId === emailValidationReqIdRef.current) {
          setEmailValidating(false);
        }
      }
    };

    // isSubmitting only disables NEXT after a re-render, so two presses in the
    // same frame would both send a verification SMS. Same guard as the entry
    // screen (RewardsSignInScreen).
    const submitInFlightRef = useRef(false);

    const onSubmit = async (values: RewardsSignUpFormData) => {
      if (submitInFlightRef.current) {
        return;
      }
      submitInFlightRef.current = true;
      setSubmitError(null);
      // DIS-1135 bug_008: invalidate any in-flight email-blur validation
      // so its late response can't setError('email') after submit has
      // navigated away.
      ++emailValidationReqIdRef.current;
      try {
        // Validate email domain via Zen API before proceeding (mirrors AccountEntryScreen)
        try {
          const validationResponse = await apiService.validateEmail(values.email || '');
          if (!validationResponse.data.valid) {
            setError('email', {
              type: 'validate',
              message: validationResponse.data.message || 'Please enter a valid email address',
            });
            return;
          }
        } catch (e) {
          logger.error('SignUp', 'Email validation failed on submit', serializeError(e));
          setError('email', {
            type: 'validate',
            message: 'Unable to validate email. Please try again.',
          });
          return;
        }

        if (!values.dateOfBirth) {
          setError('dateOfBirth', {type: 'manual', message: 'Date of birth is required'});
          return;
        }

        const zedDOB = formatDateForZen(values.dateOfBirth);

        rewardsStore.setFirstName(values.firstName || '');
        rewardsStore.setLastName(values.lastName || '');
        rewardsStore.setEmailAddress(values.email || '');
        rewardsStore.setDateOfBirth(zedDOB);

        if (!values.suburb) {
          setError('suburb', {type: 'manual', message: 'Suburb is required'});
          return;
        }

        rewardsStore.setSuburb({
          locality: values.suburb.locality || '',
          postal_code: values.suburb.postal_code || '',
          administrative_area: values.suburb.administrative_area || '',
        });

        if (rewardsStore.isStaffSignup && !rewardsStore.staffStoreId) {
          setStaffStoreError('Please select a store');
          return;
        }

        if (!rewardsStore?.phoneNumber) {
          logger.error('SignUp', 'Phone number is required');
        } else {
          const result = await apiService.rewardsVerifyNewUserPhoneNumber({
            phoneNumber: rewardsStore.phoneNumber,
            emailAddress: values.email || '',
          });

          // The user may have left (Back) while the SMS was being sent.
          if (!mountedRef.current) {
            return;
          }

          if (result?.data) {
            if (result.data.emailAlreadyExists) {
              setError('email', {
                type: 'manual',
                message:
                  'Email address already in use. Please use a different email address or contact support',
              });
            } else {
              // {"status":"pending"} is the typical response
              navigation.navigate(AppRoute.REWARDS_VERIFICATION, flowType ? {flowType} : undefined);
            }
          } else {
            // apiService returns null on an HTTP error response. The mountedRef
            // check above already covers an unmount during the request.
            setSubmitError('Could not send verification code. Please try again.');
          }
        }
      } catch (e) {
        logger.error('SignUp', 'Error in /send-verification-new-lite-user', serializeError(e));
        if (mountedRef.current) {
          setSubmitError('Could not send verification code. Please try again.');
        }
      } finally {
        submitInFlightRef.current = false;
      }
    };

    // DIS-1870 (option B): NEXT stays enabled, so a press on an incomplete form
    // must name every missing field. rewardsSignUpSchema marks dateOfBirth and
    // suburb nullable, so the resolver reports neither while it is null; these
    // errors are set here. The press stops before onSubmit, which calls the
    // email-validation API first.
    const setMissingPickerErrors = (values: RewardsSignUpFormData) => {
      let missing = false;
      if (!values.dateOfBirth) {
        setError('dateOfBirth', {type: 'manual', message: 'Date of birth is required'});
        missing = true;
      }
      if (!values.suburb) {
        setError('suburb', {type: 'manual', message: 'Suburb is required'});
        missing = true;
      }
      return missing;
    };

    const handleNextPress = handleSubmit(
      values => (setMissingPickerErrors(values) ? undefined : onSubmit(values)),
      () => {
        setMissingPickerErrors(getValues());
      },
    );

    // Renders nothing until there is an error to show. An always-present blank
    // line held the space open and pushed NEXT about 63 below the checkbox,
    // against 30-40 in Figma (536:4602).
    const renderErrorMessageWhenShown = (name: keyof RewardsSignUpFormData) => {
      logger.debug('SignUp', 'Rendering error message', {field: name, errors});
      if (!(touchedFields[name] || isSubmitted) || !errors[name]) {
        return null;
      }
      return (
        <Text style={styles.errorMessage} id={`${name}-error`}>
          {errors[name]?.message}
        </Text>
      );
    };

    return (
      <KeyboardAwareScrollView
        keyboardShouldPersistTaps="always"
        contentContainerStyle={{paddingBottom: tabBarPadding}}>
        <SignUpPageLayout
          isWide={isWide}
          onLayout={onLayout}
          testID="rewardsSignUpScreen"
          // Neither desktop frame (536:4602, 537:5246) draws a back control, and
          // the create-account page drops its chevron on wide too.
          control={
            isWide ? null : (
              <View style={localStyles.backRow}>
                <BackButton
                  onPress={() => {
                    // DIS-1318: preserve staff/signUpCode through back-nav
                    navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);
                  }}
                />
              </View>
            )
          }
          banner={<RewardsBanner />}
          // Desktop puts the banner above the heading; the mobile frame
          // (160:8335) puts it below the intro.
          bannerBelowIntro={!isWide}
          sectionPaddingTop={isWide ? undefined : NARROW_TOP_GAP}
          heading={
            <Text style={[designTokens.type.heading.h1, localStyles.heading]}>
              {`JOIN ${rewardsMarketingName.toUpperCase()}`}
            </Text>
          }
          intro={<Text style={[designTokens.type.paragraph.p2, localStyles.intro]}>{INTRO}</Text>}
          main={
            <View style={isWide ? localStyles.fieldStackWide : localStyles.fieldStack}>
              <View>
                <FormField
                  label="Mobile"
                  required
                  description={MOBILE_HELPER}
                  keyboardType="phone-pad"
                  maxLength={20}
                  // have to use rewardStore.phoneNumber
                  // initialValues.phoneNumber might not be up to date (tldr mobx)
                  value={formatMobileForDisplay(rewardsStore.phoneNumber)}
                  editable={false}
                  testID="phoneNumber"
                />
                {/* No AskToVerify: create-account shows it only while Mobile is
                  editable (AccountCreateScreenLoyaltyEnabled), and here Mobile is
                  always read-only; the description above carries the message. */}
              </View>
              {/* DIS-1824: a fieldStack child of its own, so the stack's gap applies
                above it. Nested in the Mobile View it sat flush under Mobile. */}
              {isSignUpCodeValid ? (
                <FormField
                  label="Sign up code"
                  value={rewardsStore.signUpCode}
                  editable={false}
                  testID="signUpCode"
                />
              ) : null}
              <Controller
                control={control}
                name="firstName"
                render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                  <FormField
                    label="First name"
                    required
                    autoCapitalize="words"
                    autoComplete="given-name"
                    keyboardType="default"
                    maxLength={50}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value || ''}
                    error={!!error && (isTouched || isSubmitted)}
                    errorMessage={error?.message}
                    testID="firstName"
                  />
                )}
              />
              <Controller
                control={control}
                name="lastName"
                render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                  <FormField
                    label="Last name"
                    required
                    autoCapitalize="words"
                    autoComplete="family-name"
                    keyboardType="default"
                    maxLength={50}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value || ''}
                    error={!!error && (isTouched || isSubmitted)}
                    errorMessage={error?.message}
                    testID="lastName"
                  />
                )}
              />
              <View>
                <Controller
                  control={control}
                  name="email"
                  render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                    <FormField
                      label="Email"
                      required
                      autoCapitalize="none"
                      autoComplete="email"
                      keyboardType="email-address"
                      maxLength={100}
                      onBlur={() => handleEmailBlur(onBlur, value || '')}
                      onChangeText={onChange}
                      value={value || ''}
                      error={!!error && (isTouched || isSubmitted)}
                      errorMessage={error?.message}
                      testID="email"
                    />
                  )}
                />
                {emailValidating ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: designTokens.space.s6,
                      marginTop: designTokens.space.s4,
                    }}
                    testID="email-validating-indicator">
                    <ActivityIndicator size="small" />
                    <Text variant="body2" color="textMuted">
                      Validating email…
                    </Text>
                  </View>
                ) : null}
              </View>

              <View style={styles.dateOfBirthContainer}>
                <Controller
                  control={control}
                  name="dateOfBirth"
                  render={({field: {onChange, value}, fieldState: {error, isTouched}}) => (
                    <FieldFrame
                      label="Date of birth"
                      required
                      description={DOB_HELPER}
                      error={!!error && (isTouched || isSubmitted)}
                      errorMessage={error?.message}
                      testID="dateOfBirth">
                      <DateOfBirthInput
                        initialDate={value || undefined}
                        onChange={dateString => {
                          onChange(dateString);
                        }}
                        onInvalidDate={errorMessage => {
                          setError('dateOfBirth', {type: 'manual', message: errorMessage});
                        }}
                      />
                    </FieldFrame>
                  )}
                />
              </View>

              <View style={{zIndex: 2000, elevation: 2}}>
                <Controller
                  control={control}
                  name="suburb"
                  render={({field: {onChange, value}, fieldState: {error, isTouched}}) => (
                    <FieldFrame
                      label="Suburb or Postcode"
                      required
                      error={!!error && (isTouched || isSubmitted)}
                      errorMessage={error?.message}
                      testID="suburb">
                      <SuburbPicker
                        onSelectSuburb={onChange}
                        initialSuburb={value}
                        testID="suburb-picker"
                      />
                    </FieldFrame>
                  )}
                />
              </View>

              {/* DIS-1384 R5/K1: LMG staff path renders no store field, no
                department/state field, and no PROMO CODE field (heading
                included) on this screen — selection happens on the
                dedicated LmgStaffSelectionScreen after OTP. */}
              {Boolean(!rewardsStore.isLmgStaffFlow && rewardsStore.isStaffSignup) && (
                <View>
                  <Text variant="subheading" accessibilityRole="header">
                    YOUR STORE
                  </Text>
                  <View>
                    <StaffStorePicker
                      mode="compact"
                      onStoreSelected={handleStaffStoreSelected}
                      selectedStore={selectedStaffStore}
                      testID="staff-store-picker"
                    />
                    {staffStoreError ? (
                      <Text style={styles.errorMessage}>{staffStoreError}</Text>
                    ) : null}
                  </View>
                </View>
              )}
            </View>
          }
          aside={
            <View>
              <View>
                <Controller
                  control={control}
                  name="confirmCheckbox"
                  render={({field: {onChange, value}}) => (
                    <CheckBox
                      text={
                        <>
                          <Text variant="body2">
                            When signing up to the {rewardsMarketingName} program, you agree with
                            the {textNode}
                          </Text>
                        </>
                      }
                      checked={value}
                      onChange={() => onChange(!value)}
                    />
                  )}
                />
                {renderErrorMessageWhenShown('confirmCheckbox')}
              </View>
              {dialogNode}
              <View style={localStyles.ctaRow}>
                {submitError ? (
                  <Text
                    variant="body2"
                    color="error"
                    style={{marginBottom: designTokens.space.s8}}
                    accessibilityRole="alert"
                    accessibilityLiveRegion="polite"
                    testID="signup-submit-error">
                    {submitError}
                  </Text>
                ) : null}
                <Button
                  title={'NEXT'}
                  onPress={handleNextPress}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  buttonStyle={localStyles.cta}
                  disabledStyle={localStyles.ctaDisabled}
                  titleStyle={localStyles.ctaTitle}
                  disabledTitleStyle={localStyles.ctaTitleDisabled}
                  testID="rewards-sign-up-next"
                />
              </View>
            </View>
          }
          asideAlign="top"
          stackedGap={SECTION_GAP}
        />
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsSignUp;

// SHORTCUT: spacing literals - SECTION_GAP below and the remaining literal
// margins in the render above. SECTION_GAP (mobile field gap, field stack ->
// checkbox, checkbox -> NEXT) follows the create-account form
// (AccountCreateScreenLoyaltyEnabled); Figma mobile measures 32 between fields
// and 24 on desktop. Ceiling: this screen. Upgrade path: a width-dependent
// field-gap key in layout (app/design-system/tokens/spacing.ts), a DIS-1735 epic
// decision. The colours are theme-resolved, not literals.
const SECTION_GAP = 30;
// Figma mobile Join 160:8335 draws 24 from the header to the first item.
const NARROW_TOP_GAP = designTokens.space.s24;

const localStyles = StyleSheet.create({
  backRow: {
    alignItems: 'flex-start',
  },
  heading: {
    color: theme.colour.text.heading.primary,
    textAlign: 'center',
  },
  intro: {
    textAlign: 'center',
  },
  // zIndex: SignUpPageLayout renders `aside` (checkbox, NEXT) as a later
  // sibling of `main`, so it paints over an open DOB list that extends past
  // Suburb. The DOB container's zIndex only orders it within `main`.
  fieldStack: {
    gap: SECTION_GAP,
    zIndex: 1,
  },
  // 536:4602 stacks the fields 24 apart. Mobile keeps SECTION_GAP. No layout
  // key carries a field-to-field gap that changes with width yet, so this
  // reads the scale directly.
  fieldStackWide: {
    gap: designTokens.space.s24,
    zIndex: 1,
  },
  ctaRow: {
    marginTop: SECTION_GAP,
  },
  // Same CTA as the create-account form and the Lite entry screen: height and
  // radius come from SignUpPageLayout so the three cannot drift.
  cta: {
    width: '100%',
    height: CTA_HEIGHT,
    borderRadius: CTA_RADIUS,
    backgroundColor: theme.colour.background.button.primary,
  },
  ctaDisabled: {
    backgroundColor: theme.colour.background.button.disabled,
  },
  ctaTitle: {
    ...designTokens.type.button.primary,
    color: theme.colour.background.button.contrastOnPrimary,
  },
  ctaTitleDisabled: {
    color: theme.colour.text.button.disabled,
  },
});
